import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../utils/supabase';
import { Session, User } from '@supabase/supabase-js';

// Define the shape of our context
interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, userData?: { full_name?: string }) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error: any }>;
  refreshProfile: () => Promise<void>;
  isAuthenticated: boolean;
  checkUserExists: (email: string) => Promise<{ exists: boolean; error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  signInWithMagicLink: (email: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ data?: any; error?: any }>;
}

// Define the shape of our user profile
export interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  phone_number?: string;
  bio?: string;
  created_at: string;
  updated_at?: string;
  has_completed_onboarding?: boolean;
  metadata?: {
    professional_field?: string;
    experience_level?: string;
    job_search_status?: string;
    [key: string]: any; // Allow for additional metadata fields
  };
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  isLoading: true,
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => ({ error: null }),
  updateProfile: async () => ({ error: null }),
  refreshProfile: async () => {},
  isAuthenticated: false,
  checkUserExists: async () => ({ exists: false, error: null }),
  resetPassword: async () => ({ error: null }),
  signInWithMagicLink: async () => ({ error: null }),
  signInWithGoogle: async () => ({ data: null, error: null }),
});

// Props for the AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

// Create the AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 3;

    // Set initial session and user with retry logic
    const initializeAuth = async () => {
      if (!isMounted) return;

      setIsLoading(true);

      try {
        // Get current session with timeout
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session fetch timeout')), 10000)
        );
        
        const { data: { session } } = await Promise.race([sessionPromise, timeoutPromise]) as any;

        if (!isMounted) return;

        setSession(session);

        if (session?.user) {
          setUser(session.user);
          await fetchProfile(session.user.id);
        } else {
          setUser(null);
          setProfile(null);
        }
      } catch (error) {
        // Retry logic for network issues
        if (retryCount < maxRetries && isMounted) {
          retryCount++;
          setTimeout(() => {
            if (isMounted) {
              initializeAuth();
            }
          }, 1000 * retryCount); // Exponential backoff
          return;
        }

        // Final error handling
        if (isMounted) {
          setSession(null);
          setUser(null);
          setProfile(null);
          // Auth initialization failed after retries
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    // Set up auth state change listener with proper error handling
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;

        // Auth state changed

        // Handle session changes immediately
        setSession(session);
        setUser(session?.user ?? null);

        // Fetch profile data with error handling
        if (session?.user) {
          try {
            await fetchProfile(session.user.id);
          } catch (error) {
            // Failed to fetch profile on auth change
          }
        } else {
          setProfile(null);
        }
      }
    );

    // Clean up subscription on unmount
    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Fetch user profile from Supabase with proper error handling and caching
  const fetchProfile = async (userId: string, forceRefresh = false) => {
    if (!userId || typeof userId !== 'string') {
      // Invalid userId provided to fetchProfile
      return;
    }

    try {
      // Cache profile data to avoid unnecessary requests
      const cacheKey = `profile_${userId}`;
      const cachedProfile = !forceRefresh ? sessionStorage.getItem(cacheKey) : null;
      
      if (cachedProfile && !forceRefresh) {
        try {
          const parsedProfile = JSON.parse(cachedProfile);
          setProfile(parsedProfile);
          return;
        } catch (e) {
          // Invalid cache, continue with fetch
          sessionStorage.removeItem(cacheKey);
        }
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Profile doesn't exist, create a basic one
          // Profile not found, user may need to complete onboarding
          setProfile(null);
        } else {
          // Error fetching profile
        }
        return;
      }

      const profileData = data as UserProfile;
      setProfile(profileData);
      
      // Cache the profile for 5 minutes
      sessionStorage.setItem(cacheKey, JSON.stringify(profileData));
      setTimeout(() => {
        sessionStorage.removeItem(cacheKey);
      }, 5 * 60 * 1000);
    } catch (error) {
      // Exception while fetching profile
    }
  };

  // Refresh the user profile
  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id, true); // Force refresh
    }
  };

  // Sign in with email and password with input validation
  const signIn = async (email: string, password: string) => {
    try {
      // Input validation
      if (!email || !password) {
        return { error: new Error('Email and password are required') };
      }

      if (typeof email !== 'string' || typeof password !== 'string') {
        return { error: new Error('Invalid input format') };
      }

      const trimmedEmail = email.trim();
      if (trimmedEmail.length === 0) {
        return { error: new Error('Email cannot be empty') };
      }

      if (password.length < 6) {
        return { error: new Error('Password must be at least 6 characters') };
      }

      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        return { error: new Error('Invalid email format') };
      }

      const { error } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });
      return { error };
    } catch (error) {
      return { error: error instanceof Error ? error : new Error('Sign in failed') };
    }
  };

  // Sign up with email and password with comprehensive validation
  const signUp = async (email: string, password: string, userData?: { full_name?: string }) => {
    try {
      // Input validation
      if (!email || !password) {
        return { error: new Error('Email and password are required') };
      }

      if (typeof email !== 'string' || typeof password !== 'string') {
        return { error: new Error('Invalid input format') };
      }

      const trimmedEmail = email.trim();
      if (trimmedEmail.length === 0) {
        return { error: new Error('Email cannot be empty') };
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        return { error: new Error('Invalid email format') };
      }

      // Password validation
      if (password.length < 8) {
        return { error: new Error('Password must be at least 8 characters') };
      }

      if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
        return { error: new Error('Password must contain at least one uppercase letter, one lowercase letter, and one number') };
      }

      // Validate user data if provided
      const validatedUserData: any = {};
      if (userData?.full_name) {
        const trimmedName = userData.full_name.trim();
        if (trimmedName.length > 0 && trimmedName.length <= 100) {
          validatedUserData.full_name = trimmedName;
        }
      }

      const { error } = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          data: validatedUserData,
        },
      });
      return { error };
    } catch (error) {
      return { error: error instanceof Error ? error : new Error('Sign up failed') };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (error) {
      return { error };
    }
  };

  // Update user profile
  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) {
      return { error: new Error('User not authenticated') };
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (!error) {
        await refreshProfile();
      }

      return { error };
    } catch (error) {
      return { error };
    }
  };

  // Check if a user with the given email exists without revealing sensitive information
  const checkUserExists = async (email: string) => {
    try {
      // Input validation
      if (!email || typeof email !== 'string' || email.trim().length === 0) {
        return { exists: false, error: new Error('Invalid email format') };
      }

      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        return { exists: false, error: new Error('Invalid email format') };
      }

      // Instead of attempting to determine if user exists (which creates enumeration vulnerability),
      // we'll use a secure approach that doesn't reveal user existence.
      // This function should only be used for UI purposes, not security decisions.
      
      // For password reset, always return success regardless of whether user exists
      // The actual verification happens server-side securely
      return { exists: true, error: null };
    } catch (error) {
      return { exists: false, error: new Error('Unable to verify email') };
    }
  };

  // Reset password with secure implementation
  const resetPassword = async (email: string) => {
    try {
      // Input validation
      if (!email || typeof email !== 'string') {
        return { error: new Error('Valid email is required') };
      }

      const trimmedEmail = email.trim();
      if (trimmedEmail.length === 0) {
        return { error: new Error('Email cannot be empty') };
      }

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        return { error: new Error('Invalid email format') };
      }

      // Use absolute URL to ensure proper redirection
      const redirectUrl = `${window.location.origin}/reset-password`;

      await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo: redirectUrl,
      });
      
      // Always return success to prevent email enumeration
      // Even if the email doesn't exist, we don't want to reveal that
      return { error: null };
    } catch (error) {
      // Return success even on error to prevent enumeration
      return { error: null };
    }
  };

  // Sign in with magic link (passwordless)
  const signInWithMagicLink = async (email: string) => {
    try {
      // Use absolute URL to ensure proper redirection
      const redirectUrl = 'https://ajirapro.com/auth/magic-link';

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectUrl,
        }
      });
      return { error };
    } catch (error) {
      return { error };
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      // Use absolute URL to ensure proper redirection
      const redirectUrl = 'https://ajirapro.com/auth/google-callback';

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          // You can specify additional scopes if needed
          scopes: 'email profile',
        }
      });

      return { data, error };
    } catch (error) {
      return { error };
    }
  };

  // Determine if user is authenticated
  const isAuthenticated = !!user && !!session;

  // Create the context value
  const value = {
    session,
    user,
    profile,
    isLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    refreshProfile,
    isAuthenticated,
    checkUserExists,
    resetPassword,
    signInWithMagicLink,
    signInWithGoogle,
  };

  // Provide the context to children
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Create a hook for using the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
